/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useContext, useState, Fragment } from 'react';
import { useQuery } from 'react-apollo';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import AkTabs, { TabList as AkTabList } from '@atlaskit/tabs';
import { WidthObserver } from '@atlaskit/width-detector';

import { Attribution, ErrorDisplay, TransparentErrorBoundary } from '@confluence/error-boundary';
import type { RoutesContextType } from '@confluence/route-manager';
import { RoutesContext } from '@confluence/route-manager';
import { SpacePermissionsSharedHeader } from '@confluence/space-permissions/entry-points/SpacePermissionsSettingsTab';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { SpaceSettingsBreadcrumbs } from '@confluence/breadcrumbs/entry-points/SpaceSettingsBreadcrumbs';

import {
	useSelectedNavigationWebItems,
	FORGE_SPACE_SETTINGS_NAVIGATION_PARENT_WEBITEM,
	isSpacePermissionsRoute,
	isHubPermissionsRoute,
} from '../helpers/webItem';
import { isNonNullable } from '../utils';
import {
	AUTOMATION_WEBITEM_KEY,
	useSpaceSettingsWebItemSectionsFromQuery,
} from '../web-item-sections/useSpaceSettingsWebItemSectionsFromQuery';

import type {
	SpaceSettingsNavigationV1SpaceSettingsNavigationQuery as SpaceSettingsNavigationV1SpaceSettingsNavigationQuery$data,
	SpaceSettingsNavigationV1SpaceSettingsNavigationQueryVariables as SpaceSettingsNavigationV1SpaceSettingsNavigationQuery$variables,
} from './__types__/SpaceSettingsNavigationV1SpaceSettingsNavigationQuery';
import type { ForgeAppsProps } from './ForgeAppTabs';
import { TabsController } from './TabsController';
import { SpaceSettingsFilterBar } from './SpaceSettingsFilterBar';

const SpaceSettingsNavigationV1SpaceSettingsNavigationQuery = gql`
	query SpaceSettingsNavigationV1SpaceSettingsNavigationQuery($spaceKey: String) {
		webItemSections(location: "system.space.tools", key: $spaceKey) {
			id
			label
			cacheKey
			items {
				id
				completeKey
				label
				url
				icon {
					path
				}
			}
		}
		space(key: $spaceKey) {
			alias
			id
			key
			currentUser {
				isAdmin
			}
		}
	}
`;
export const ANALYTICS_SOURCE = 'spaceSettingsV2InnerTabs';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NavContainer = styled.div({
	width: '100%',
	margin: `${token('space.100')} 0 0 ${token('space.100')}`,
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndFilterBar = styled.div({
	display: 'flex',
	flexFlow: 'row wrap',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > h1:first-of-type': {
		margin: `${token('space.250')} 0`,
	},
});

// Margin styles for this in TitleAndFilterBar above.
// h1:first-of-type margins are set to 0 elsewhere
// and need to be set here with greater specificity.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTitle = styled.h1({
	font: token('font.heading.large'),
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarContainer = styled.div({
	margin: `15px ${token('space.100')} 0 0`,
});

export type SpaceSettingsNavigationProps = ForgeAppsProps & {
	children?: (props: { withCompleteNav: boolean }) => React.ReactNode;
	onLoadForgeApps?: Function;
	onLoadForgeError?: (Error) => void;
};

/**
 * Renders Confluence Space Settings V2 tab navigation, including Connect and Forge apps.
 * This navigation may be used both on legacy monolith SSR space settings pages (where
 * it is injected via React portal), and on fully React SPA space settings pages (currently
 * only the Forge app space settings page).
 * This component handles only rendering the navigation tabs, not page contents.
 */
export const SpaceSettingsNavigationV1: FC<SpaceSettingsNavigationProps> = ({
	children,
	spaceKey,
	onLoadForgeApps,
	forgeAppId,
	forgeEnvId,
	forgeRoutePath,
	onLoadForgeError,
}) => {
	const [width, setWidth] = useState<number>(0);
	const onResize = debounce(setWidth, 200, { leading: false });
	const { location, matchRoute } = useContext<RoutesContextType>(RoutesContext);

	const { data, loading, error } = useQuery<
		SpaceSettingsNavigationV1SpaceSettingsNavigationQuery$data,
		SpaceSettingsNavigationV1SpaceSettingsNavigationQuery$variables
	>(SpaceSettingsNavigationV1SpaceSettingsNavigationQuery, {
		variables: {
			spaceKey,
		},
	});
	const webItemSections = useSpaceSettingsWebItemSectionsFromQuery(data);

	const { innerNavigationWebItem, outerNavigationWebItem } = useSelectedNavigationWebItems({
		matchRoute,
		location,
		webItemSections,
		skip: loading || !!error,
	});

	if (loading) {
		return null;
	} else if (error) {
		// Something went wrong; report it
		return <ErrorDisplay error={error} />;
	}

	// we only show forge apps under one specific space settings page
	const showForgeAppTabs =
		outerNavigationWebItem?.id === FORGE_SPACE_SETTINGS_NAVIGATION_PARENT_WEBITEM;

	const showTitleAndNavTabs = innerNavigationWebItem?.completeKey !== AUTOMATION_WEBITEM_KEY;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const isSpaceOrHubPermsPage =
		isSpacePermissionsRoute(matchRoute, location) || isHubPermissionsRoute(matchRoute, location);

	return (
		<NavContainer>
			{webItemSections?.filter(isNonNullable).map((webItemSection) =>
				webItemSection.id === outerNavigationWebItem?.id ? (
					<Fragment key={webItemSection.id}>
						<TransparentErrorBoundary
							attribution={isCompanyHub ? Attribution.COMPANY_HUB : Attribution.INHERIT}
						>
							<SpaceSettingsBreadcrumbs
								spaceAlias={data?.space?.alias}
								spaceKey={spaceKey}
								source={ANALYTICS_SOURCE}
								currentSection={webItemSection.id}
							/>
						</TransparentErrorBoundary>
						{showTitleAndNavTabs ? (
							<Fragment>
								<TitleAndFilterBar>
									<StyledTitle>{webItemSection.label}</StyledTitle>
									{!isCompanyHub && (
										<FilterBarContainer>
											<SpaceSettingsFilterBar
												spaceAlias={data?.space?.alias}
												spaceKey={spaceKey}
												source={ANALYTICS_SOURCE}
												forgeAppId={forgeAppId}
												forgeEnvId={forgeEnvId}
												forgeRoutePath={forgeRoutePath}
											/>
										</FilterBarContainer>
									)}
								</TitleAndFilterBar>
								{isSpaceOrHubPermsPage ? (
									<SpacePermissionsSharedHeader spaceKey={spaceKey} />
								) : null}
								<Box paddingInlineStart="space.100">
									<AkTabs id="space-settings-inner-nav-tabs">
										<AkTabList>
											<TabsController
												webItems={webItemSection.items?.filter(isNonNullable)}
												width={width}
												innerNavigationWebItem={innerNavigationWebItem}
												// Forge app props
												showForgeAppTabs={showForgeAppTabs}
												spaceKey={spaceKey}
												onLoadForgeApps={onLoadForgeApps}
												forgeAppId={forgeAppId}
												forgeEnvId={forgeEnvId}
												forgeRoutePath={forgeRoutePath}
												onLoadForgeError={onLoadForgeError}
											/>
										</AkTabList>
									</AkTabs>
								</Box>
							</Fragment>
						) : null}
						{typeof children === 'function'
							? children({
									withCompleteNav: showTitleAndNavTabs,
								})
							: children}
					</Fragment>
				) : null,
			)}
			<WidthObserver setWidth={onResize} offscreen />
		</NavContainer>
	);
};
